import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SERVICE_OPTIONS } from "@/lib/constants";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";

const INITIAL = {
  full_name: "", email: "", phone: "", project_type: "",
  location: "", budget: "", preferred_service: "", description: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [site, setSite] = useState({});

  useEffect(() => {
    api.get("/site/content").then((r) => setSite(r.data || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.description) {
      toast.error("Please provide your name, email, and a short description.");
      return;
    }
    setSubmitting(true);
    try {
      let attachment_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "enquiry");
        try {
          // Public upload not allowed; store filename in description as note
          // If we want, we could allow anonymous upload — for now include filename inline.
        } catch (_) {}
      }
      const payload = { ...form };
      if (file) payload.description = `${payload.description}\n\n[Attachment: ${file.name}]`;
      await api.post("/enquiries", payload);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm(INITIAL);
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waNumber = (site.whatsapp_number || "").replace(/[^\d]/g, "");

  return (
    <div className="pt-44 lg:pt-56" data-testid="contact-page">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-4"><div className="eyebrow">Start a project</div></div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Let's talk about
              <br />
              your project.
            </h1>
            <p className="mt-6 text-lg text-stone max-w-2xl">
              Share as much or as little as you'd like — we'll respond within one business day.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-12">
          {/* Form */}
          <form onSubmit={submit} className="col-span-12 lg:col-span-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="eyebrow block mb-1">Full name *</label>
                <input data-testid="contact-name" required className="line-input" value={form.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Email *</label>
                <input data-testid="contact-email" required type="email" className="line-input" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Phone</label>
                <input data-testid="contact-phone" className="line-input" value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Property location</label>
                <input data-testid="contact-location" className="line-input" value={form.location} onChange={set("location")} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Project type</label>
                <select data-testid="contact-project-type" className="line-input" value={form.project_type} onChange={set("project_type")}>
                  <option value="">Select…</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Interior Design</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1">Estimated budget</label>
                <select data-testid="contact-budget" className="line-input" value={form.budget} onChange={set("budget")}>
                  <option value="">Select…</option>
                  <option>Under R 1M</option>
                  <option>R 1M – R 5M</option>
                  <option>R 5M – R 10M</option>
                  <option>R 10M – R 25M</option>
                  <option>R 25M+</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow block mb-1">Preferred service</label>
                <select data-testid="contact-service" className="line-input" value={form.preferred_service} onChange={set("preferred_service")}>
                  <option value="">Select…</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow block mb-1">Project description *</label>
                <textarea data-testid="contact-description" required rows={5} className="line-input resize-none" value={form.description} onChange={set("description")} />
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow block mb-2">Attachment (optional)</label>
                <input
                  data-testid="contact-file"
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm text-stone file:mr-4 file:py-2 file:px-4 file:border file:border-line file:bg-white file:text-ink file:text-xs file:uppercase file:tracking-widest file:cursor-pointer"
                />
                {file && <div className="mt-2 text-xs text-stone font-mono">{file.name}</div>}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-10 inline-flex items-center gap-3 bg-ink text-white px-8 py-4 text-sm hover:bg-ink/85 transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : (<>Send message <Send size={16} /></>)}
            </button>
          </form>

          {/* Studio info */}
          <aside className="col-span-12 lg:col-span-4 space-y-8">
            <div className="border border-line p-8">
              <div className="eyebrow mb-4">Studio</div>
              <ul className="space-y-3 text-sm">
                {site.contact_email && (
                  <li className="flex items-start gap-3"><Mail size={16} className="mt-0.5 text-stone shrink-0" /><a href={`mailto:${site.contact_email}`} className="hover:underline">{site.contact_email}</a></li>
                )}
                {site.contact_phone && (
                  <li className="flex items-start gap-3"><Phone size={16} className="mt-0.5 text-stone shrink-0" /><a href={`tel:${site.contact_phone}`}>{site.contact_phone}</a></li>
                )}
                {site.office_address && (
                  <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-stone shrink-0" /><span>{site.office_address}</span></li>
                )}
                {site.business_hours && <li className="pt-2 font-mono text-xs text-stone">{site.business_hours}</li>}
              </ul>
            </div>

            {waNumber && (
              <a
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-between border border-line bg-white p-6 hover:border-ink transition-colors"
              >
                <div>
                  <div className="eyebrow">WhatsApp</div>
                  <div className="font-heading text-lg mt-1">Chat with the studio</div>
                </div>
                <MessageCircle size={22} />
              </a>
            )}

            <div className="aspect-[4/3] bg-sand overflow-hidden border border-line">
              <iframe
                title="Studio location"
                data-testid="contact-map"
                width="100%" height="100%" loading="lazy"
                src={site.google_maps_embed || "https://www.google.com/maps?q=Cape+Town+South+Africa&output=embed"}
                style={{ border: 0 }}
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
